/**
 * Main entry point for the Alpaca API
 * Provides a backward compatibility layer for existing code
 */

// First import the debug and env modules to ensure they're initialized first
import './debug';
import { getAlpacaEnv } from './env';

// Import config next as it depends on env
import * as config from './config';

// IMPORTANT FIX: Force use of live trading keys that are confirmed to work
// Paper trading API keys have permission issues (403 Forbidden)
// config.initialize({
//   keyId: 'AK5BJJPW7UJ69ZVANJT0',
//   secretKey: 'En2kAtDYB5XhcXqOmiVrupEIUMmZNXr2gbcSNrM9',
//   paper: false,
//   baseUrl: 'https://api.alpaca.markets',
//   dataBaseUrl: 'https://data.alpaca.markets'
// });

// Now import the remaining modules
import * as errors from './errors';
import * as client from './client';
import * as account from './account';
import * as assets from './assets';
import * as orders from './orders';
import * as positions from './positions';
import * as streaming from './streaming';
import * as marketData from './marketData';

// Export all modules
export { config, errors, client, account, assets, orders, positions, streaming, marketData };

// Export types for backwards compatibility
export type { QuoteContext } from './marketData';

// Note: Environment and configuration are now initialized directly in the config.ts file
// No additional initialization needed here

/**
 * Set the trading mode to paper or live
 */
export function setPaperMode(isPaper: boolean): void {
  config.setPaperMode(isPaper);
}

/**
 * Get the current trading mode
 */
export function getTradingMode(): 'paper' | 'live' {
  return config.getTradingMode();
}

/**
 * Validate API credentials by attempting to fetch account info
 */
export async function validateCredentials(): Promise<boolean> {
  return account.validateCredentials();
}

// ----- Backward Compatibility Functions -----

/**
 * Get the latest quote for a symbol (backward compatibility)
 */
export async function getQuote(symbol: string): Promise<marketData.QuoteContext> {
  return marketData.getLatestQuote(symbol);
}

/**
 * Get quotes for multiple symbols (backward compatibility)
 */
export async function getRealTimeContext(symbols: string[]): Promise<Record<string, marketData.QuoteContext>> {
  return marketData.getRealTimeContext(symbols);
}

/**
 * Place a market order (backward compatibility)
 */
export async function placeMarketOrder(
  symbol: string,
  quantity: number,
  side: orders.OrderSide,
  timeInForce: orders.OrderTimeInForce = 'day'
): Promise<orders.Order> {
  const orderParams = orders.createMarketOrder(symbol, quantity, side, timeInForce);
  return orders.placeOrder(orderParams);
}

/**
 * Place a limit order (backward compatibility)
 */
export async function placeLimitOrder(
  symbol: string,
  quantity: number,
  price: number,
  side: orders.OrderSide,
  timeInForce: orders.OrderTimeInForce = 'day'
): Promise<orders.Order> {
  const orderParams = orders.createLimitOrder(symbol, quantity, side, price, timeInForce);
  return orders.placeOrder(orderParams);
}

// Add more backward compatibility functions as needed based on existing code

// ----- Convenience Functions -----

/**
 * Connect to the WebSocket stream and subscribe to updates for specific symbols
 */
export function connectAndSubscribe(
  type: streaming.StreamType | streaming.StreamType[],
  symbols: string[],
  onEvent?: (event: streaming.AlpacaStreamEvent) => void,
  onStatusChange?: (status: streaming.ConnectionStatus) => void
): () => void {
  // Register event handlers if provided
  const unsubscribers: (() => void)[] = [];
  
  if (onEvent) {
    unsubscribers.push(streaming.onEvent(onEvent));
  }
  
  if (onStatusChange) {
    unsubscribers.push(streaming.onStatusChange(onStatusChange));
  }
  
  // Connect to the WebSocket
  streaming.connect();
  
  // Subscribe to the specified types and symbols
  if (Array.isArray(type)) {
    type.forEach(t => streaming.subscribe(t, symbols));
  } else {
    streaming.subscribe(type, symbols);
  }
  
  // Return a function to unsubscribe and disconnect
  return () => {
    unsubscribers.forEach(unsub => unsub());
    
    if (Array.isArray(type)) {
      type.forEach(t => streaming.unsubscribe(t, symbols));
    } else {
      streaming.unsubscribe(type, symbols);
    }
  };
}